import { html, nothing, render } from 'lit-html';

import { isSafari } from '@disy/lib/browser';
import { requireNonNullable } from '@disy/lib/custom-error';
import type { DelegateEvent } from '@disy/lib/event-util';
import { on } from '@disy/lib/event-util';
import { supportsTouch } from '@disy/lib/media';
import { px } from '@disy/lib/px';
import { isKeyboardNavigation } from '@disy/lib/setup-keynav';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';
import { createButton } from '@disy/ui/button';
import { closeAllPopups } from '@disy/ui/popup';

import { repositoryService } from 'cadenza/repository/repository-service';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import { getAnalysisContextIcon } from 'cadenza/workbook/header/workbook-header';
import type { View } from 'cadenza/workbook/view/view';
import type { UpdateableElement } from 'cadenza/workbook/view/view-filters-button/view-filters-button';
import i18n from 'cadenza/workbook/view/view-filters-button/view-filters-button.properties';
import {
  createDragHandleButton,
  createEnlargeButton,
  createExportButton,
  createMaximizeButton,
  createMinimizeButton,
  createMoreActionsButton,
  createSeparatedButtonGroup,
  createViewActionsToolbarEditButton,
  createViewFiltersToolbarButton,
  createViewInfoButton,
  createVisualizationPicker,
} from 'cadenza/workbook/view/view-header/view-actions-toolbar-button-provider';
import { titlebarVisibilitySetting } from 'cadenza/workbook/view/view-header/view-toolbar-utils';
import {
  getConditionsByViewId,
  getDetailViewId,
  getMaximizedViewId,
  hasForceMaximizedView,
  isActiveView,
  isAnalysisContextAffectingViews,
  isDetailView,
  isIntegratedView,
  isViewLoading,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import { togglePanelVisibility } from 'cadenza/workbook/workbook.state';
import { isLinearLayout, worksheetLayout } from 'cadenza/workbook/worksheet-layout';
import { canBeEnlargedInLinearLayout } from 'cadenza/workbook/worksheet/worksheet-grid/worksheet-layouting-util';

import './view-actions-toolbar.css';

// Offset for the toolbar top position when it's displayed above the view (grid-layout).
const ABOVE_OFFSET_TOP = 10;
// Offset for the toolbar top position when it's displayed inside the view without workbook header (linear-layout).
const INSIDE_OFFSET_TOP = 50;
// Space between header and toolbar.
const HEADER_TOOLBAR_GAP = 8;
const COMPONENT_NAME = 'd-view-actions-toolbar';

export class ViewActionsToolbar extends HTMLElement {
  readonly #view;
  readonly #isContentView: boolean;
  #tearDowns: Teardown[] = [];
  #viewFiltersButton?: UpdateableElement;
  #viewFilterMenuItem?: UpdateableElement;
  #resizeObserver?: ResizeObserver;
  #isMaximized: boolean = false;
  #dragHandleButton?: HTMLButtonElement;
  #moreActionsButton?: UpdateableElement;
  #viewInfoButton?: UpdateableElement;

  constructor(view: View, { isContentView }: { isContentView: boolean } = { isContentView: false }) {
    super();
    this.#view = view;
    this.#isContentView = isContentView;
  }

  async connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    if (isSafari) {
      this.classList.add('safari');
    }
    await this.#assignButtons();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateInternal(true);
    });
    this.#render();
    this.#tearDowns = [
      observeStore(getStore(), hasForceMaximizedView, (disabled) => {
        Array.from(this.querySelectorAll<HTMLButtonElement>(`.${COMPONENT_NAME}--button:not(.force-enabled)`)).forEach(
          (button) => {
            button.disabled = disabled;
          },
        );
      }),
      observeStore(getStore(), getMaximizedViewId, (maximizedViewId) => {
        if (this.#isMaximized || maximizedViewId === this.#view.viewId) {
          this.#isMaximized = maximizedViewId === this.#view.viewId;
          this.#updateInternal();

          // if keyboard navigation is active
          if (isKeyboardNavigation()) {
            if (!this.#isMaximized) {
              // set focus on maximized button
              this.querySelector<HTMLButtonElement>('.d-worksheet-view--maximize')?.focus();
            } else {
              // set focus on minimize button
              this.querySelector<HTMLButtonElement>('.d-worksheet-view--minimize')?.focus();
            }
          }
        }
      }),
      // update the local filters button on any condition change
      observeStore(getStore(), getConditionsByViewId, () => this.#updateInternal()),
      on(this, 'pointerdown', (e) => {
        closeAllPopups();
        this.#onPointerEvents(e);
      }),
      on(this, ['pointermove', 'pointerup', 'dragenter', 'dragover', 'dragleave'], (e) => {
        this.#onPointerEvents(e);
      }),
      on(this.#view, ['layout-changed'], () => {
        this.#updatePosition();
      }),
      // update the toolbar when the view finished loading
      observeStore(
        getStore(),
        (state) => isViewLoading(state, this.#view.viewId),
        (isLoading) => this.#updateButtonsAvailabilityAfterLoading(isLoading),
      ),
      on(worksheetLayout, 'change', () => {
        this.#render();
      }),
    ];
    this.#setupScrollListenerForLinearLayout();
    this.resizeObserver.observe(this.#view);

    if (supportsTouch) {
      // On touch devices the action-toolbar might overlap clickable content of the workbook toolbar
      // while being transparent (invisible) and therefore receiving unwanted touch events.
      // -> only receive touch events after the view is activated (action-toolbar is visible then)
      observeStore(
        getStore(),
        (state) => isActiveView(state, this.viewId),
        (result) => {
          this.toggleAttribute('inert', !result);
        },
      );
    }
  }

  async #assignButtons() {
    this.#viewFiltersButton = createViewFiltersToolbarButton(this.#view);
    this.#dragHandleButton = createDragHandleButton();
    this.#viewFilterMenuItem = undefined;

    const writableRepositories = await repositoryService().listWritable();
    this.#moreActionsButton = await createMoreActionsButton(
      this.#view,
      this.#isContentView,
      writableRepositories,
      () => {
        const result = [this.#createAnalysisContextToolbarButton()];
        if (isLinearLayout()) {
          this.#viewFilterMenuItem = createViewFiltersToolbarButton(this.#view, false);
          result.push(this.#viewFilterMenuItem);
        }
        return result;
      },
    );
    this.#viewInfoButton = createViewInfoButton(this.viewId);
  }

  #createAnalysisContextToolbarButton() {
    if (!isWorkbookFeatureAvailable(getStore().getState(), 'workbook-analysis-context')) {
      return;
    }
    const button: UpdateableElement = createButton(
      [
        getAnalysisContextIcon(isAnalysisContextAffectingViews(getStore().getState())),
        i18n('d-analysis-context-panel.toggle'),
      ],
      {
        attrs: { role: 'menuitem' },
        onclick: () => getStore().dispatch(togglePanelVisibility(WorkbookPanelName.ANALYSIS_CONTEXT)),
      },
    );
    this.#tearDowns.push(
      observeStore(
        getStore(),
        (state) => getDetailViewId(state),
        (detailViewId) => {
          button.hidden = !detailViewId;
        },
      ),
      observeStore(getStore(), isAnalysisContextAffectingViews, (state) => {
        button.firstElementChild!.replaceWith(getAnalysisContextIcon(state));
      }),
    );
    return button;
  }

  #updatePosition({ inside, offset } = { inside: false, offset: ABOVE_OFFSET_TOP }) {
    if (this.classList.contains('is-floating') && this.isConnected) {
      const viewRect = this.#view.getBoundingClientRect();
      this.style.left = px(viewRect.left + viewRect.width / 2);
      this.style.top = px((inside ? 0 : viewRect.top) + offset);
      this.style.transform = 'translateY(-100%) translateX(-50%)';
    }
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #teardown() {
    this.#tearDowns = tearDown(this.#tearDowns);
    this.#resizeObserver?.disconnect();
  }

  // eslint-disable-next-line no-restricted-syntax
  async #render() {
    const isInDetailView = isDetailView(getStore().getState(), this.#view.viewId);
    this.classList.toggle('white-bg', !isInDetailView);
    this.#updateInternal();
    // render a reduced version of the titlebar in case the titlebar is disabled, but the view was maximized
    if (titlebarVisibilitySetting(this.#view) === 'off' && isInDetailView) {
      render(html`${canBeEnlargedInLinearLayout(this.#view) ? createEnlargeButton(this.#view) : nothing}`, this);
      return;
    }

    // start the items with separator always, the first one (the obsolete one) will be hidden by css
    // (it is done to simplify the logic of hiding/showing the buttons)
    render(
      html`
        ${createSeparatedButtonGroup([this.#dragHandleButton], 'drag-handle')}
        ${createSeparatedButtonGroup([
          getButtonIfNotHidden(this.#viewInfoButton),
          createViewActionsToolbarEditButton(this.#view),
          createVisualizationPicker(this.#view),
          await createExportButton(this.#view),
          getButtonIfNotHidden(this.#viewFiltersButton),
          getButtonIfNotHidden(this.#moreActionsButton),
        ])}
        ${!this.#isContentView && !isLinearLayout() ?
          createSeparatedButtonGroup([createMaximizeButton(this.viewId), createMinimizeButton(this.viewId)])
        : nothing}
        ${isLinearLayout() && canBeEnlargedInLinearLayout(this.#view) ?
          createSeparatedButtonGroup([createEnlargeButton(this.#view)])
        : nothing}
      `,
      this,
    );
  }

  async update() {
    this.#render();
  }

  #updateInternal(hasBeenResized = false) {
    if (isIntegratedView(getStore().getState(), this.#view.viewId)) {
      // for integrated view we do not use this toolbar, the code is here only to avoid problems with disconnecting from DOM done in the View instance
      // (connectedCallback of this toolbar is called by some reason on worksheet toolbar remove())
      this.#teardown();
      return;
    }

    // not floating, but embedded next to the title bar for small screens and maximized states ('is-detail-view' is present)
    const isFloating = !this.#view.classList.contains('is-detail-view');
    this.classList.toggle('is-floating', isFloating);
    // also adjust max-width of title of view toolbar because the action toolbar will be positioned absolute on top-right when not floating
    const titleElement = this.#view.querySelector<HTMLElement>(`.d-worksheet-view--title`);
    if (titleElement) {
      titleElement.style.maxWidth = isFloating ? '' : `calc(100% - ${px(this.clientWidth)})`;
    }

    // Resizing is not relevant to these update methods.
    if (!hasBeenResized) {
      this.#viewFiltersButton?.update?.(this.#view);
      this.#viewFilterMenuItem?.update?.(this.#view);
      this.#viewInfoButton?.update?.(this.#view);
    }
    this.#moreActionsButton?.update?.(this.#view);

    this.#updatePosition();
  }

  get viewId() {
    return this.#view.viewId;
  }

  get resizeObserver() {
    return requireNonNullable(this.#resizeObserver);
  }

  #updateButtonsAvailabilityAfterLoading(isMapViewLoading = false) {
    if (!isMapViewLoading) {
      this.#moreActionsButton?.update?.(this.#view);
    }
  }

  #setupScrollListenerForLinearLayout() {
    let isScrolling = false;
    // The timeout is needed for Safari does not support the 'scrollend' event yet.
    let scrollEndTeardown: Teardown[] = [];
    this.#tearDowns.push(
      on(document, 'scroll', () => {
        if (!isLinearLayout()) {
          return;
        }
        if (!isScrolling) {
          tearDown(scrollEndTeardown);
          this.hidden = true;
          scrollEndTeardown.push(
            setTimeout(() => {
              this.positionForLinearLayout();
              isScrolling = false;
              scrollEndTeardown = tearDown(scrollEndTeardown);
            }, 250),
          );
        }
        isScrolling = true;
      }),
    );
    if (isLinearLayout()) {
      this.positionForLinearLayout();
    }
  }

  positionForLinearLayout() {
    const header = document.querySelector<HTMLElement>('.d-workbook-header');
    let offset = ABOVE_OFFSET_TOP;
    let inside = false;
    const viewRect = this.#view.getBoundingClientRect();
    const toolbarHeight = getActionToolbarHeight(this);
    if (header?.classList.contains('is-sticky-pinned')) {
      const toolbarOffset = header.getBoundingClientRect().height + toolbarHeight + HEADER_TOOLBAR_GAP;
      // If there is a header, and it is currently sticky and the toolbar would be below the header
      // and there is enough space to show the toolbar.
      if (
        viewRect.top < toolbarOffset &&
        viewRect.height + viewRect.y - toolbarHeight > toolbarHeight - HEADER_TOOLBAR_GAP
      ) {
        offset = toolbarOffset;
        inside = true;
      }
      // Reposition the toolbar to be visible if the top of the view is scrolled out of sight
      // and there is enough space at the bottom of the view (= don't display the toolbar inside
      // another view).
    } else if (viewRect.y < toolbarHeight && viewRect.height + viewRect.y > INSIDE_OFFSET_TOP) {
      offset = INSIDE_OFFSET_TOP;
      inside = true;
    }
    this.#updatePosition({ inside, offset });
    this.hidden = false;
  }

  #onPointerEvents(e: DelegateEvent<Event>) {
    const isDragButton = this.#dragHandleButton?.contains(e.target as Node);
    if (!isDragButton) {
      e.stopImmediatePropagation();
    }
  }
}

customElements.define(COMPONENT_NAME, ViewActionsToolbar);

/**
 * Returns the height of the toolbar regardless of it's hidden state.
 */
function getActionToolbarHeight(toolbar: HTMLElement) {
  if (!toolbar.hidden) {
    return toolbar.getBoundingClientRect().height;
  }
  const isHidden = toolbar.hidden;
  const visibility = toolbar.style.visibility;
  // If the toolbar is hidden, set visibility to hidden to prevent flickering.
  toolbar.style.visibility = 'hidden';
  toolbar.hidden = false;
  const height = toolbar.getBoundingClientRect().height;
  toolbar.hidden = isHidden;
  toolbar.style.visibility = visibility;
  return height;
}

/**
 * Returns the element only, if it's not hidden. This is needed, because the vertical separator styling relies on elements
 * not available in the DOM when they should not be visible.
 * This mechanism is needed for the correct usage of createSeparatedButtonGroup.
 *
 * @param element UpdateableElement to check and return, if it's not hidden
 */
function getButtonIfNotHidden(element: UpdateableElement | undefined) {
  if (!element || element.hidden) {
    return;
  }
  return element;
}
